import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Checkbox — high-contrast across ALL mood themes.
 *
 * Feb 27, 2026 (per provider screenshot): the previous default used
 * `data-[state=checked]:bg-primary` which inherited the active theme's
 * primary color. In several mood themes (calm-gold, default-orange, etc.)
 * the primary color was so close to the muted border that providers
 * literally could not tell at a glance whether a setting was on or off.
 *
 * Fix: hard-coded `bg-emerald-500` for checked and a thicker
 * `border-slate-400` for unchecked, regardless of mood theme. The check
 * glyph is bold white with a drop shadow so it pops on every background.
 */

const Checkbox = React.forwardRef(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-[18px] w-[18px] shrink-0 rounded-[4px] border-2 shadow-sm transition-all",
      "border-slate-400/70 bg-slate-900/40",
      "hover:border-slate-300 hover:bg-slate-800/60",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-300 data-[state=checked]:text-white data-[state=checked]:shadow-md data-[state=checked]:shadow-emerald-500/40",
      className
    )}
    {...props}>
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-white")}>
      <Check className="h-3.5 w-3.5" strokeWidth={3.5} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
